import {
  Avatar,
  Flexbox,
  ListManager,
  PageHeader,
  Paginator,
  Styles,
} from '@/components'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import usePageTitle from '@/hooks/usePageTitle'
import useQuery from '@/hooks/useQuery'
import { ANIMAL } from '@/lib/enums'
import dateMask from '@/lib/masks/date'
import stalls from '@/services/api/stalls'
import PropTypes from 'prop-types'
import React from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
  Arrow, Bottom, Flow, Identity, Item, Meta, PlaceChip, Top,
} from './styles'

function sameId(left, right) {
  return String(left) === String(right)
}

function stallText(stall) {
  return `${stall.name} (${stall.code})`
}

function animalLine(animal) {
  const species = animal?.category?.description
  const breed = animal?.breed?.description
  const gender = animal?.gender ? ANIMAL.gender.t(animal.gender) : ''
  return [species, breed, gender].filter(Boolean).join(' · ')
}

function Movement({ item, stallId }) {
  const entered = sameId(item.toStall?.id, stallId)
  const tone = entered ? 'add' : 'remove'

  return (
    <Flow>
      <PlaceChip>
        {item.fromStall ? stallText(item.fromStall) : 'Entrada'}
      </PlaceChip>
      <Arrow $tone={tone} />
      <PlaceChip $tone={tone}>
        {item.toStall ? stallText(item.toStall) : 'Saída'}
      </PlaceChip>
    </Flow>
  )
}

const stallShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
  code: PropTypes.string,
})

const nullableStall = PropTypes.oneOfType([stallShape, PropTypes.oneOf([null])])

Movement.propTypes = {
  item: PropTypes.shape({
    fromStall: nullableStall,
    toStall: nullableStall,
  }).isRequired,
  stallId: PropTypes.string.isRequired,
}

function Responsible({ person }) {
  const name = person?.fullName
  if (!name) return <span className="text-small">—</span>

  return (
    <Flexbox.Columns className="align-center col-gapless">
      <Flexbox.Column className="col-auto">
        <Avatar
          avatarSize="sm"
          name={name}
          photoURL={person.photoURL}
          alt={name}
        />
      </Flexbox.Column>
      <Flexbox.Column className="ml-2 text-tiny" style={{ minWidth: 0 }}>
        <div className="text-ellipsis">{name}</div>
      </Flexbox.Column>
    </Flexbox.Columns>
  )
}

Responsible.propTypes = {
  person: PropTypes.shape({
    fullName: PropTypes.string,
    photoURL: PropTypes.string,
  }),
}

Responsible.defaultProps = {
  person: null,
}

function History({ title }) {
  usePageTitle({ title })
  const { id } = useParams()
  const location = useLocation()
  const query = useQuery()
  const stall = location.state?.stall
  const heading = stall?.name ? `Histórico · ${stall.name}` : 'Histórico'

  const { loading, data, error } = useAxiosRequest(stalls.history, id, query)

  return (
    <Styles.Container className="grid-xl">
      <PageHeader
        title={heading}
        icon="fas fa-history"
        subtitle={stall?.code ? `Código ${stall.code}` : ''}
        rightAction={() => (
          <Link to="/app/stalls" className="btn btn-sm">
            <i className="fas fa-arrow-left" />
            &nbsp;Voltar
          </Link>
        )}
      />
      <ListManager
        loading={loading}
        count={data?.count || 0}
        error={error}
        emptyMessage="Nenhuma movimentação nesta baia"
        emptySubtitle=""
        emptyIcon="fas fa-history"
      >
        {
          Boolean(data) && (
            <div>
              {
                data.rows.map((item) => (
                  <Item key={item.id}>
                    <Top>
                      <Avatar
                        avatarSize="md"
                        name={item.animal?.name || 'Animal'}
                        photoURL={item.animal?.photoURL}
                        alt={item.animal?.name || 'Animal'}
                      />
                      <Identity>
                        <div className="text-small text-bold text-ellipsis">{item.animal?.name}</div>
                        <div className="text-tiny text-ellipsis">{animalLine(item.animal)}</div>
                      </Identity>
                      <Meta>
                        <div className="text-tiny">{dateMask(item.createdAt)}</div>
                      </Meta>
                    </Top>
                    <Bottom>
                      <Movement item={item} stallId={id} />
                      <Responsible person={item.person} />
                    </Bottom>
                  </Item>
                ))
              }
              <div className="my-2">
                <Paginator paginatedList={data} />
              </div>
            </div>
          )
        }
      </ListManager>
    </Styles.Container>
  )
}

History.propTypes = {
  title: PropTypes.string.isRequired,
}

export default History
